import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Inject, OnDestroy, ViewChild } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Observable, Subject, Subscription, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, finalize } from 'rxjs/operators';

/** ORCA-1971: página de uma busca no servidor (lista grande, ex.: clientes) */
export interface OaPickerPage<T = any> {
    options: OaPickerOption<T>[];
    hasMore: boolean;
}

export interface OaPickerOption<T = any> {
    value: T;
    title: string;
    /** Linha secundária (ex.: CPF/CNPJ, e-mail) */
    meta?: string;
    /** Miniatura (ex.: layout do modelo) */
    thumb?: string | null;
    /** Texto extra usado só na busca (ex.: documento sem máscara) */
    searchText?: string;
}

export interface OaPickerData<T = any> {
    title: string;
    options: OaPickerOption<T>[];
    selected?: T | null;
    searchPlaceholder?: string;
    /** Sem busca quando a lista é curta (ex.: modelos) */
    searchable?: boolean;
    emptyText?: string;
    /** Botão de criação no rodapé (ex.: "Novo cliente") */
    createLabel?: string;
    createIcon?: string;
    /**
     * Opcional (ORCA-1922): quando a lista está vazia ou a busca não acha nada, mostra a criação
     * no meio do estado vazio. Com busca, usa este texto trocando {term} pelo que foi digitado
     * (ex.: 'Criar cliente "{term}"'). O termo volta em `result.term`.
     */
    createFromSearchLabel?: string;
    /**
     * Opcional (ORCA-1971): busca no servidor, página a página, no lugar de filtrar `options`.
     * As páginas seguintes carregam ao rolar até o fim da lista.
     */
    search?: (term: string, page: number) => Observable<OaPickerPage<T>>;
}

export type OaPickerResult<T = any> = { action: 'select'; value: T } | { action: 'create'; term?: string } | undefined;

/**
 * Seletor em tela cheia para telas compactas (ver oaDialogConfig): busca fixa no topo,
 * lista rolável com linhas de 56px e ações fixas embaixo.
 */
@Component({
    selector: 'app-oa-picker-dialog',
    templateUrl: './oa-picker-dialog.component.html',
    styleUrls: ['./oa-picker-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OaPickerDialogComponent<T = any> implements AfterViewInit, OnDestroy {
    @ViewChild('searchInput') searchInput?: ElementRef<HTMLInputElement>;
    @ViewChild('list') list?: ElementRef<HTMLElement>;

    term = '';
    filtered: OaPickerOption<T>[];

    // Busca no servidor (ORCA-1971)
    loading = false;
    private page = 0;
    private hasMore = false;
    private request?: Subscription;
    private readonly terms$ = new Subject<string>();
    private readonly termsSubscription?: Subscription;

    constructor(
        public dialogRef: MatDialogRef<OaPickerDialogComponent<T>, OaPickerResult<T>>,
        @Inject(MAT_DIALOG_DATA) public data: OaPickerData<T>,
        private cdr: ChangeDetectorRef,
    ) {
        this.filtered = data.options.slice();
        if (data.search) {
            this.termsSubscription = this.terms$
                .pipe(debounceTime(300), distinctUntilChanged())
                .subscribe(() => this.loadPage(1));
            this.loadPage(1);
        }
    }

    ngOnDestroy(): void {
        this.request?.unsubscribe();
        this.termsSubscription?.unsubscribe();
    }

    private loadPage(page: number): void {
        if (!this.data.search) {
            return;
        }
        this.request?.unsubscribe();
        this.loading = true;
        this.cdr.markForCheck();
        this.request = this.data.search(this.term.trim(), page).pipe(
            catchError(() => of({ options: [], hasMore: false } as OaPickerPage<T>)),
            finalize(() => {
                this.loading = false;
                this.cdr.markForCheck();
            }),
        ).subscribe((result) => {
            this.page = page;
            this.hasMore = result.hasMore;
            const options = page === 1 ? result.options : [...this.filtered, ...result.options];
            this.filtered = options;
            this.cdr.markForCheck();
        });
    }

    /** Rolou até perto do fim: próxima página */
    onScroll(target: HTMLElement): void {
        if (!this.data.search || this.loading || !this.hasMore) {
            return;
        }
        if (target.scrollTop + target.clientHeight >= target.scrollHeight - 120) {
            this.loadPage(this.page + 1);
        }
    }

    ngAfterViewInit(): void {
        // Mostra a opção atual sem obrigar a rolar; não abre o teclado sozinho.
        setTimeout(() => {
            const current = this.list?.nativeElement.querySelector('[aria-selected="true"]') as HTMLElement | null;
            current?.scrollIntoView({ block: 'center' });
        });
    }

    get searchable(): boolean {
        return this.data.searchable !== false;
    }

    onSearch(term: string): void {
        this.term = term;
        if (this.data.search) {
            this.terms$.next(term.trim());
            return;
        }
        const text = normalize(term);
        const digits = term.replace(/\D/g, '');
        this.filtered = !text
            ? this.data.options.slice()
            : this.data.options.filter((option) => {
                const haystack = normalize(`${option.title} ${option.meta || ''} ${option.searchText || ''}`);
                return haystack.includes(text)
                    || (digits.length > 0 && haystack.replace(/\D/g, '').includes(digits));
            });
    }

    clearSearch(): void {
        this.onSearch('');
        this.searchInput?.nativeElement.focus();
    }

    isSelected(option: OaPickerOption<T>): boolean {
        return this.data.selected != null && option.value === this.data.selected;
    }

    select(option: OaPickerOption<T>): void {
        this.dialogRef.close({ action: 'select', value: option.value });
    }

    create(): void {
        const term = this.term.trim();
        this.dialogRef.close(term ? { action: 'create', term } : { action: 'create' });
    }

    /** Botão de criação dentro do estado vazio (só com createFromSearchLabel). */
    get emptyCreateLabel(): string | null {
        if (!this.data.createLabel || !this.data.createFromSearchLabel) {
            return null;
        }
        const term = this.term.trim();
        return term ? this.data.createFromSearchLabel.replace('{term}', term) : this.data.createLabel;
    }

    trackByIndex(index: number): number {
        return index;
    }
}

function normalize(value: string): string {
    return (value || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').trim();
}
